import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, Eye, Tag, FolderOpen, User, LayoutTemplate } from "lucide-react";

export default function ResearchDashboard() {
  const [stats, setStats] = useState({ articles: 0, categories: 0, tags: 0, authors: 0, published: 0, drafts: 0, scheduled: 0, media: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [arts, cats, tags, authors, media] = await Promise.all([
          api.get("/admin/articles"),
          api.get("/admin/article-categories"),
          api.get("/admin/article-tags"),
          api.get("/admin/article-authors"),
          api.get("/admin/media"),
        ]);
        const items = arts.data || [];
        setStats({
          articles: items.length,
          published: items.filter((a) => a.status === "published").length,
          drafts: items.filter((a) => a.status === "draft").length,
          scheduled: items.filter((a) => a.status === "scheduled").length,
          categories: (cats.data || []).length,
          tags: (tags.data || []).length,
          authors: (authors.data || []).length,
          media: (media.data || []).length,
        });
        setRecent(items.slice(0, 5));
      } catch {}
    })();
  }, []);

  const tile = (label, value, Icon, to) => (
    <Link to={to} className="border border-slate-200 rounded-md bg-white p-4 hover:border-blue-400 transition-colors block" data-testid={`rh-tile-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="w-8 h-8 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center"><Icon className="w-4 h-4 text-blue-700" strokeWidth={1.75}/></div>
        <div className="text-3xl font-display font-semibold">{value}</div>
      </div>
      <div className="text-xs text-slate-500 mt-3 font-mono uppercase tracking-widest">{label}</div>
    </Link>
  );

  return (
    <div data-testid="research-dashboard-page">
      <div className="mb-6">
        <div className="eyebrow mb-2">Research Hub</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Dashboard</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tile("Articles", stats.articles, FileText, "/admin/research/articles")}
        {tile("Published", stats.published, Eye, "/admin/research/articles")}
        {tile("Drafts", stats.drafts, FileText, "/admin/research/articles")}
        {tile("Scheduled", stats.scheduled, FileText, "/admin/research/articles")}
        {tile("Categories", stats.categories, FolderOpen, "/admin/research/categories")}
        {tile("Tags", stats.tags, Tag, "/admin/research/tags")}
        {tile("Authors", stats.authors, User, "/admin/research/authors")}
        {tile("Media", stats.media, LayoutTemplate, "/admin/research/media")}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-semibold">Recently updated</h2>
          <Link to="/admin/research/articles" className="text-sm text-blue-700 hover:text-blue-800">All articles →</Link>
        </div>
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {recent.length === 0 ? <div className="text-sm text-slate-500 p-8 text-center">No articles yet.</div> : recent.map((a) => (
            <Link key={a.slug} to={`/admin/research/articles/${a.slug}`} className="flex items-center justify-between p-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
              <div>
                <div className="text-sm text-slate-900">{a.title}</div>
                <div className="text-xs text-slate-500 font-mono">{a.slug} · {a.status}</div>
              </div>
              <div className="text-xs text-slate-500">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : ""}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
